/**
 * The page numbers a pager shows, in a row whose width never changes.
 *
 * The old copies of this (one per pager) grew the row as you paged: page 1 was
 * `1 2 … 68`, page 4 was `1 … 4 5 6 … 68`. Three slots appeared mid-click, the
 * row re-laid out, and the button under the cursor moved. Here the slot count
 * is fixed for a given `totalPages`, so Next always lands where Next was.
 */
export type PageWindowSlot = number | 'ellipsis'

/** `1 … 4 5 6 … 68` — first, last, the current page and a neighbour each side. */
export const PAGE_WINDOW_SLOTS = 7

const range = (from: number, to: number): number[] =>
  Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => from + i)

export function buildPageWindow(
  currentPage: number,
  totalPages: number,
  slots: number = PAGE_WINDOW_SLOTS,
): PageWindowSlot[] {
  if (!Number.isFinite(totalPages) || totalPages < 1) return []
  const total = Math.floor(totalPages)
  const width = Math.max(5, Math.floor(slots))
  if (total <= width) return range(1, total)

  const current = Math.min(Math.max(Math.floor(currentPage) || 1, 1), total)
  // Four slots are spoken for: first, last and the two ellipses. The rest go
  // to the current page and its neighbours.
  const siblings = Math.max(1, Math.floor((width - 5) / 2))
  const left = Math.max(current - siblings, 1)
  const right = Math.min(current + siblings, total)
  // An ellipsis that would hide exactly one page costs the same room as the
  // page itself, so the edge block absorbs it instead.
  const leftDots = left > 3
  const rightDots = right < total - 2

  if (!leftDots && rightDots) return [...range(1, width - 2), 'ellipsis', total]
  if (leftDots && !rightDots) return [1, 'ellipsis', ...range(total - (width - 3), total)]
  return [1, 'ellipsis', ...range(left, right), 'ellipsis', total]
}

/**
 * A short window centred on the page you are on, with the far end of the book
 * kept in view — and it turns around at the end rather than running out of room.
 *
 * Going forward the page sits between its neighbours and the last two pages
 * stay reachable: `1 2 3 … 16 17` on pages 1 and 2, `2 3 4 … 16 17` on page 3.
 * Once the block would reach those last two it turns: the FIRST two pages are
 * the ones held open instead — `1 2 … 13 14 15`, `1 2 … 15 16 17`. Either way
 * the row is the same width, it reads the same coming back as going out, and
 * whichever end is out of the window is one click away on « or ».
 */
export function buildSlidingPageWindow(
  currentPage: number,
  totalPages: number,
  size: number = 3,
  anchor: number = 2,
): PageWindowSlot[] {
  if (!Number.isFinite(totalPages) || totalPages < 1) return []
  const total = Math.floor(totalPages)
  const width = Math.max(1, Math.floor(size))
  const anchors = Math.max(0, Math.floor(anchor))
  // Few enough pages that a window would hide nothing worth hiding — and too
  // few for a block to sit clear of both ends.
  if (total <= width + anchors + 2) return range(1, total)

  const current = Math.min(Math.max(Math.floor(currentPage) || 1, 1), total)
  const tailStart = total - anchors + 1
  // The page keeps its neighbour on each side; at the very ends the block stops
  // rather than hanging off the book.
  const start = Math.min(Math.max(current - Math.floor(width / 2), 1), total - width + 1)
  const end = start + width - 1
  // Turn around before the block runs into the last two pages, not after:
  // letting them touch would print five numbers in a row where every other page
  // prints three and an ellipsis. The second test keeps the turn from happening
  // so early that the block swallows the anchors it is supposed to sit apart
  // from.
  const turn = end + 1 >= tailStart && start > anchors + 1

  const pages = turn
    ? [...range(1, anchors), ...range(start, end)]
    : [...range(start, end), ...range(tailStart, total)]

  const sorted = [...new Set(pages)].sort((a, b) => a - b)
  const slots: PageWindowSlot[] = []
  sorted.forEach((page, i) => {
    const previous = sorted[i - 1]
    if (previous !== undefined && page - previous > 1) slots.push('ellipsis')
    slots.push(page)
  })
  return slots
}
